import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowBigDown,
  ArrowBigUp,
  ExternalLink,
  Loader,
  LogIn,
  MessageSquareText,
  Pencil,
  Reply,
  Send,
  Trash2,
  UserPlus,
} from 'lucide-react';
import { AccountsApiError, getEmailFromIdToken } from '@/lib/apiAccounts';
import { createAuthPath } from '@/lib/authNavigation';
import { buildPlanningApplicationPath } from '@/lib/planningApplicationUrl';
import {
  addDiscussionComment,
  deleteDiscussionComment,
  getDiscussionThread,
  getDiscussionThreadPayload,
  hydrateDiscussion,
  getDiscussionUserVote,
  getDiscussionVoteScore,
  subscribeToDiscussionUpdates,
  type PlanningDiscussionComment,
  updateDiscussionComment,
  type PlanningDiscussionThreadNode,
  toggleDiscussionVote,
} from '@/lib/services/planningDiscussionsService';

type AuthStatus = 'checking' | 'signed-in' | 'signed-out';

// #1 – draft persistence: use localStorage with a 24-hour TTL instead of sessionStorage
const DISCUSSION_DRAFT_PREFIX = 'cityscope_discussion_draft:';
const DISCUSSION_AUTH_RETURN_KEY = 'cityscope_auth_return_url';
const DISCUSSION_DRAFT_MAX_AGE_MS = 1000 * 60 * 60 * 24; // 24 h

interface PlanningDiscussionPanelProps {
  reference: string;
  council?: string;
  /** Optional council portal URL passed from the parent modal's application.link */
  councilUrl?: string;
}

interface DiscussionCurrentUser {
  userId: string;
  displayName: string;
  email?: string | null;
}

interface DiscussionCommentItemProps {
  authStatus: AuthStatus;
  busyCommentId: string | null;
  comment: PlanningDiscussionThreadNode;
  currentUser: DiscussionCurrentUser | null;
  depth: number;
  editingCommentId: string | null;
  editingDraft: string;
  onDeleteComment: (commentId: string) => void;
  onEditComment: (commentId: string) => void;
  onReplyDraftChange: (value: string) => void;
  onReplyToComment: (commentId: string) => void;
  onRequireAuth: (message: string) => void;
  onSaveEdit: () => void;
  onSubmitReply: (parentId: string) => void;
  onUpdateEditingDraft: (value: string) => void;
  onVote: (commentId: string, value: 'up' | 'down') => void;
  replyingToCommentId: string | null;
  replyDraft: string;
  resetEditState: () => void;
  resetReplyState: () => void;
}

// ─── Pure helpers ────────────────────────────────────────────────────────────

function getDiscussionBody(comment: Pick<PlanningDiscussionComment, 'body' | 'status'>): string {
  return comment.status === 'deleted' ? 'Comment deleted.' : comment.body;
}

function formatAuthorLabel(email?: string | null, fallback?: string | null): string {
  const rawEmail = typeof email === 'string' ? email.trim().toLowerCase() : '';
  if (rawEmail.includes('@')) {
    const localPart = rawEmail.split('@')[0] || '';
    const cleaned = localPart.replace(/[^a-z0-9._-]/gi, '').replace(/^[._-]+|[._-]+$/g, '');
    if (cleaned) return cleaned;
  }
  const base = String(fallback || rawEmail || '').trim().toLowerCase();
  if (!base) return 'You';
  let hash = 0;
  for (let i = 0; i < base.length; i += 1) {
    hash = (hash * 31 + base.charCodeAt(i)) >>> 0;
  }
  return `City Scope user ${hash.toString(16).slice(0, 6).padStart(6, '0').toUpperCase()}`;
}

function getAuthorInitials(label?: string | null): string {
  const parts = String(label || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return 'CS';
  return parts.slice(0, 2).map((p) => p[0]?.toUpperCase() || '').join('');
}

// #6 – relative timestamps computed at call-time (caller refreshes on a timer)
function formatRelativeTimestamp(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Just now';
  const diffMs = Date.now() - date.getTime();
  const diffMin = Math.round(diffMs / 60_000);
  if (diffMin < 1) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.round(diffHr / 24);
  if (diffDay < 7) return `${diffDay}d ago`;
  return new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }).format(date);
}

function formatFullTimestamp(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric', month: 'long', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  }).format(date);
}

function getAccountsApiDetail(error: unknown): { status?: number; detail?: string } {
  if (error instanceof AccountsApiError) {
    const body = error.body && typeof error.body === 'object' ? (error.body as Record<string, unknown>) : null;
    return { status: error.status, detail: typeof body?.detail === 'string' ? body.detail : undefined };
  }
  return {};
}

function getFriendlyDuplicateMessage(detail?: string) {
  return detail || 'That looks like a duplicate — the same comment was posted recently. Try editing it, or wait a moment before posting an update.';
}

function getDiscussionDraftKey(reference: string): string {
  return `${DISCUSSION_DRAFT_PREFIX}${String(reference || '').trim().toUpperCase()}`;
}

function buildDiscussionReturnUrl(reference: string): string | undefined {
  if (typeof window === 'undefined') return undefined;
  try {
    const path = buildPlanningApplicationPath(reference);
    const url = new URL(path, window.location.origin);
    url.searchParams.set('tab', 'discussion');
    return url.toString();
  } catch {
    return window.location.href;
  }
}

// #8 – localStorage draft persistence with 24h TTL
function saveDiscussionDraftState(
  reference: string,
  draftState: { draft: string; replyingToCommentId: string | null; replyDraft: string }
) {
  if (typeof window === 'undefined' || !reference) return;
  const hasDraft = draftState.draft.trim() || draftState.replyDraft.trim();
  if (!hasDraft) return;
  try {
    window.localStorage.setItem(
      getDiscussionDraftKey(reference),
      JSON.stringify({ ...draftState, createdAt: Date.now() })
    );
  } catch {
    // localStorage may fail in private browsing / quota exceeded
    try {
      window.sessionStorage.setItem(
        getDiscussionDraftKey(reference),
        JSON.stringify({ ...draftState, createdAt: Date.now() })
      );
    } catch { /* noop */ }
  }
}

function loadDiscussionDraftState(reference: string): {
  draft: string; replyingToCommentId: string | null; replyDraft: string;
} | null {
  if (typeof window === 'undefined' || !reference) return null;
  const key = getDiscussionDraftKey(reference);
  const storages = [window.localStorage, window.sessionStorage];
  for (const storage of storages) {
    try {
      const raw = storage.getItem(key);
      if (!raw) continue;
      storage.removeItem(key);
      const parsed = JSON.parse(raw) as { draft?: unknown; replyingToCommentId?: unknown; replyDraft?: unknown; createdAt?: unknown };
      const createdAt = typeof parsed.createdAt === 'number' ? parsed.createdAt : 0;
      if (!createdAt || Date.now() - createdAt > DISCUSSION_DRAFT_MAX_AGE_MS) continue;
      return {
        draft: typeof parsed.draft === 'string' ? parsed.draft : '',
        replyingToCommentId: typeof parsed.replyingToCommentId === 'string' && parsed.replyingToCommentId.trim() ? parsed.replyingToCommentId : null,
        replyDraft: typeof parsed.replyDraft === 'string' ? parsed.replyDraft : '',
      };
    } catch { /* noop */ }
  }
  return null;
}

function rememberDiscussionAuthReturn(reference: string) {
  if (typeof window === 'undefined') return undefined;
  const returnUrl = buildDiscussionReturnUrl(reference);
  if (returnUrl) {
    try { window.sessionStorage.setItem(DISCUSSION_AUTH_RETURN_KEY, returnUrl); } catch { /* noop */ }
  }
  return returnUrl;
}

function countThreadNodes(nodes: PlanningDiscussionThreadNode[]): number {
  return nodes.reduce((total, node) => total + 1 + countThreadNodes(node.replies), 0);
}

function countReplyDescendants(node: PlanningDiscussionThreadNode): number {
  return countThreadNodes(node.replies);
}

function getDepthSpacing(depth: number): string {
  if (depth <= 0) return '';
  return 'ml-5 border-l-2 border-[var(--c-border)]/60 pl-3 sm:ml-7 sm:pl-4';
}

// #3 – auto-grow helper: syncs textarea height to its scroll height
function autoGrow(el: HTMLTextAreaElement | null) {
  if (!el) return;
  el.style.height = 'auto';
  el.style.height = `${el.scrollHeight}px`;
}

// ─── Comment item ─────────────────────────────────────────────────────────────

function DiscussionCommentItem({
  authStatus,
  busyCommentId,
  comment,
  currentUser,
  depth,
  editingCommentId,
  editingDraft,
  onDeleteComment,
  onEditComment,
  onReplyDraftChange,
  onReplyToComment,
  onRequireAuth,
  onSaveEdit,
  onSubmitReply,
  onUpdateEditingDraft,
  onVote,
  replyingToCommentId,
  replyDraft,
  resetEditState,
  resetReplyState,
}: DiscussionCommentItemProps) {
  const isOwner = comment.canEdit || comment.canDelete;
  const isEditing = editingCommentId === comment.id;
  const isReplying = replyingToCommentId === comment.id;
  const isBusy = busyCommentId === comment.id;
  const activeVote = currentUser ? getDiscussionUserVote(comment) : null;
  const isRootComment = depth === 0;
  const replyCount = countReplyDescendants(comment);
  const voteScore = getDiscussionVoteScore(comment);
  const authorName = isOwner && currentUser?.displayName ? currentUser.displayName : comment.author.displayName;
  const authorInitials = getAuthorInitials(authorName);
  const isDeleted = comment.status === 'deleted';

  // #6 – live-updating relative timestamp
  const [relativeTime, setRelativeTime] = useState(() => formatRelativeTimestamp(comment.createdAt));
  useEffect(() => {
    const id = setInterval(() => setRelativeTime(formatRelativeTimestamp(comment.createdAt)), 30_000);
    return () => clearInterval(id);
  }, [comment.createdAt]);

  // #1 – inline delete confirmation state
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  // #3 – auto-grow refs for edit + reply textareas
  const editTextareaRef = useRef<HTMLTextAreaElement>(null);
  const replyTextareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => { if (isEditing) autoGrow(editTextareaRef.current); }, [isEditing, editingDraft]);
  useEffect(() => { if (isReplying) autoGrow(replyTextareaRef.current); }, [isReplying, replyDraft]);

  // #2 – Cmd/Ctrl+Enter to submit in edit or reply textarea
  const handleEditKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') { e.preventDefault(); onSaveEdit(); }
    if (e.key === 'Escape') { e.preventDefault(); resetEditState(); }
  };
  const handleReplyKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') { e.preventDefault(); onSubmitReply(comment.id); }
    if (e.key === 'Escape') { e.preventDefault(); resetReplyState(); }
  };

  return (
    <div className={getDepthSpacing(depth)}>
      <article
        className={`group ${isRootComment ? 'py-4' : 'py-3'} grid grid-cols-[auto_minmax(0,1fr)] gap-3`}
      >
        {/* Avatar */}
        <div className="pt-0.5">
          <div
            className={`flex items-center justify-center rounded-full border font-semibold uppercase tracking-[0.12em] ${
              isRootComment
                ? 'h-9 w-9 border-[var(--c-accent)]/30 bg-[var(--c-accent)]/12 text-[11px] text-[var(--c-accent)]'
                : 'h-7 w-7 border-[var(--c-border)] bg-[var(--c-surface)] text-[10px] text-[var(--c-text-secondary)]'
            }`}
          >
            {authorInitials}
          </div>
        </div>

        <div className="min-w-0">
          {/* Meta row */}
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-[11px] text-[var(--c-text-tertiary)]">
            <span className="text-sm font-semibold text-[var(--c-text-primary)]">{authorName}</span>
            {/* #5 – "you" badge */}
            {isOwner && !isDeleted ? (
              <span className="rounded-full border border-[var(--c-accent)]/25 bg-[var(--c-accent)]/10 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-[0.16em] text-[var(--c-accent)]">
                you
              </span>
            ) : null}
            {/* #6 – relative timestamp with full date on hover */}
            <time
              dateTime={comment.createdAt}
              title={formatFullTimestamp(comment.createdAt)}
              className="cursor-default"
            >
              {relativeTime}
            </time>
            {comment.updatedAt !== comment.createdAt ? (
              <span className="italic text-[var(--c-text-tertiary)]">edited</span>
            ) : null}
          </div>

          {/* Body / edit form */}
          {isEditing ? (
            <div className="mt-2 rounded-[18px] border border-[var(--c-accent)]/35 bg-[var(--c-surface)] p-2.5 ring-1 ring-[var(--c-accent)]/12">
              <textarea
                ref={editTextareaRef}
                value={editingDraft}
                onChange={(e) => { onUpdateEditingDraft(e.target.value); autoGrow(e.target); }}
                onKeyDown={handleEditKeyDown}
                rows={3}
                className="min-h-[88px] w-full resize-none overflow-hidden rounded-[12px] border border-[var(--c-border)] bg-[var(--c-bg)] px-3 py-2.5 text-sm leading-6 text-[var(--c-text-primary)] outline-none transition-colors duration-150 focus:border-[var(--c-accent)]"
              />
              <div className="mt-1 text-right text-[10px] text-[var(--c-text-tertiary)]">⌘↵ to save · Esc to cancel</div>
              <div className="mt-2 flex items-center justify-end gap-2 text-sm">
                <button type="button" onClick={resetEditState} className="rounded-full px-3 py-1.5 text-xs font-medium text-[var(--c-text-secondary)] transition-colors duration-150 hover:bg-[var(--c-bg)] hover:text-[var(--c-text-primary)]">
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={onSaveEdit}
                  disabled={isBusy || editingDraft.trim().length === 0}
                  className="inline-flex items-center justify-center gap-1.5 rounded-full bg-[var(--c-accent)] px-3 py-1.5 text-xs font-semibold text-[var(--c-bg)] transition-all duration-150 hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {isBusy ? <Loader size={12} className="animate-spin" /> : <Send size={12} />}
                  Save changes
                </button>
              </div>
            </div>
          ) : isDeleted ? (
            <p className="mt-1 whitespace-pre-wrap text-sm italic leading-6 text-[var(--c-text-tertiary)]">
              {getDiscussionBody(comment)}
            </p>
          ) : (
            <p className="mt-1 whitespace-pre-wrap text-sm leading-6 text-[var(--c-text-primary)]/95">
              {comment.body}
            </p>
          )}

          {/* Action bar */}
          {!isEditing ? (
            <div className="mt-2 flex flex-wrap items-center gap-1 text-[11px]">
              {/* Vote pill */}
              <div className="inline-flex items-center rounded-full bg-[var(--c-bg)]/70 ring-1 ring-inset ring-[var(--c-border)]">
                <button
                  type="button"
                  onClick={() => onVote(comment.id, 'up')}
                  disabled={!currentUser || isBusy || isDeleted}
                  className={`inline-flex h-7 w-7 items-center justify-center rounded-full transition-colors duration-150 ${activeVote === 'up' ? 'text-[var(--c-accent)]' : 'text-[var(--c-text-secondary)] hover:text-[var(--c-text-primary)]'} disabled:cursor-not-allowed disabled:opacity-40`}
                  aria-label="Upvote" title={currentUser ? 'Upvote' : 'Sign in to vote'}
                >
                  <ArrowBigUp size={15} />
                </button>
                <span className={`min-w-[1.5rem] px-1 text-center text-xs font-semibold ${voteScore > 0 ? 'text-[var(--c-accent)]' : voteScore < 0 ? 'text-[var(--c-error)]' : 'text-[var(--c-text-secondary)]'}`}>
                  {voteScore > 0 ? `+${voteScore}` : voteScore}
                </span>
                <button
                  type="button"
                  onClick={() => onVote(comment.id, 'down')}
                  disabled={!currentUser || isBusy || isDeleted}
                  className={`inline-flex h-7 w-7 items-center justify-center rounded-full transition-colors duration-150 ${activeVote === 'down' ? 'text-[var(--c-error)]' : 'text-[var(--c-text-secondary)] hover:text-[var(--c-text-primary)]'} disabled:cursor-not-allowed disabled:opacity-40`}
                  aria-label="Downvote" title={currentUser ? 'Downvote' : 'Sign in to vote'}
                >
                  <ArrowBigDown size={15} />
                </button>
              </div>

              {!isDeleted && isRootComment ? (
                <button
                  type="button"
                  onClick={() => { if (!currentUser) { onRequireAuth('Sign in to reply.'); return; } onReplyToComment(comment.id); }}
                  disabled={authStatus === 'checking'}
                  className="inline-flex h-7 items-center gap-1.5 rounded-full px-2.5 text-xs font-medium text-[var(--c-text-secondary)] transition-colors duration-150 hover:bg-[var(--c-bg)]/70 hover:text-[var(--c-text-primary)] disabled:opacity-50"
                >
                  <Reply size={12} /> Reply
                </button>
              ) : null}

              {replyCount > 0 ? (
                <span className="inline-flex h-7 items-center gap-1 px-2 text-xs font-medium text-[var(--c-text-tertiary)]">
                  <MessageSquareText size={11} />
                  {replyCount} {replyCount === 1 ? 'reply' : 'replies'}
                </span>
              ) : null}

              {/* #1 – inline delete confirm / owner actions */}
              {isOwner && !isDeleted ? (
                <div className="ml-auto flex items-center gap-0.5 opacity-0 transition-opacity duration-150 group-hover:opacity-100 focus-within:opacity-100">
                  {confirmingDelete ? (
                    <>
                      <span className="mr-1 text-[11px] text-[var(--c-text-secondary)]">Delete?</span>
                      <button
                        type="button"
                        onClick={() => { setConfirmingDelete(false); onDeleteComment(comment.id); }}
                        disabled={isBusy}
                        className="inline-flex h-7 items-center rounded-full bg-[var(--c-error)]/12 px-2.5 text-xs font-semibold text-[var(--c-error)] transition-colors hover:bg-[var(--c-error)]/20 disabled:opacity-50"
                      >
                        {isBusy ? <Loader size={11} className="animate-spin" /> : 'Yes, delete'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirmingDelete(false)}
                        className="inline-flex h-7 items-center rounded-full px-2.5 text-xs font-medium text-[var(--c-text-secondary)] transition-colors hover:bg-[var(--c-bg)]/70 hover:text-[var(--c-text-primary)]"
                      >
                        Cancel
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={() => onEditComment(comment.id)}
                        className="inline-flex h-7 w-7 items-center justify-center rounded-full text-[var(--c-text-secondary)] transition-colors duration-150 hover:bg-[var(--c-bg)]/70 hover:text-[var(--c-accent)]"
                        aria-label="Edit" title="Edit"
                      >
                        <Pencil size={12} />
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirmingDelete(true)}
                        className="inline-flex h-7 w-7 items-center justify-center rounded-full text-[var(--c-text-secondary)] transition-colors duration-150 hover:bg-[var(--c-bg)]/70 hover:text-[var(--c-error)]"
                        aria-label="Delete" title="Delete"
                      >
                        <Trash2 size={12} />
                      </button>
                    </>
                  )}
                </div>
              ) : null}
            </div>
          ) : null}

          {/* Reply composer */}
          {isReplying ? (
            <div className="mt-3 rounded-[18px] border border-[var(--c-accent)]/30 bg-[var(--c-surface)] p-2.5 ring-1 ring-[var(--c-accent)]/10">
              <div className="text-[11px] text-[var(--c-text-tertiary)]">
                Replying to{' '}
                <span className="font-medium text-[var(--c-text-primary)]">{authorName}</span>
              </div>
              <textarea
                ref={replyTextareaRef}
                value={replyDraft}
                onChange={(e) => { onReplyDraftChange(e.target.value); autoGrow(e.target); }}
                onKeyDown={handleReplyKeyDown}
                rows={2}
                placeholder="Write a reply..."
                autoFocus
                className="mt-2 min-h-[64px] w-full resize-none overflow-hidden rounded-[12px] border border-[var(--c-border)] bg-[var(--c-bg)] px-3 py-2.5 text-sm leading-6 text-[var(--c-text-primary)] outline-none transition-colors duration-150 placeholder:text-[var(--c-text-tertiary)] focus:border-[var(--c-accent)]"
              />
              <div className="mt-1 text-right text-[10px] text-[var(--c-text-tertiary)]">⌘↵ to post · Esc to cancel</div>
              <div className="mt-2 flex items-center justify-end gap-2 text-sm">
                <button type="button" onClick={resetReplyState} className="rounded-full px-3 py-1.5 text-xs font-medium text-[var(--c-text-secondary)] transition-colors duration-150 hover:bg-[var(--c-bg)] hover:text-[var(--c-text-primary)]">
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => onSubmitReply(comment.id)}
                  disabled={isBusy || replyDraft.trim().length === 0}
                  className="inline-flex items-center justify-center gap-1.5 rounded-full bg-[var(--c-accent)] px-3 py-1.5 text-xs font-semibold text-[var(--c-bg)] transition-all duration-150 hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {isBusy ? <Loader size={12} className="animate-spin" /> : <Send size={12} />}
                  Post reply
                </button>
              </div>
            </div>
          ) : null}

          {/* Nested replies */}
          {comment.replies.length > 0 ? (
            <div className="mt-2">
              {comment.replies.map((reply) => (
                <DiscussionCommentItem
                  key={reply.id}
                  authStatus={authStatus}
                  busyCommentId={busyCommentId}
                  comment={reply}
                  currentUser={currentUser}
                  depth={depth + 1}
                  editingCommentId={editingCommentId}
                  editingDraft={editingDraft}
                  onDeleteComment={onDeleteComment}
                  onEditComment={onEditComment}
                  onReplyDraftChange={onReplyDraftChange}
                  onReplyToComment={onReplyToComment}
                  onRequireAuth={onRequireAuth}
                  onSaveEdit={onSaveEdit}
                  onSubmitReply={onSubmitReply}
                  onUpdateEditingDraft={onUpdateEditingDraft}
                  onVote={onVote}
                  replyingToCommentId={replyingToCommentId}
                  replyDraft={replyDraft}
                  resetEditState={resetEditState}
                  resetReplyState={resetReplyState}
                />
              ))}
            </div>
          ) : null}
        </div>
      </article>
    </div>
  );
}

// ─── Main panel ───────────────────────────────────────────────────────────────

export default function PlanningDiscussionPanel({ reference, council = 'edinburgh', councilUrl }: PlanningDiscussionPanelProps) {
  const normalizedReference = useMemo(
    () => String(reference || '').replace(/\s+/g, '').trim().toUpperCase(),
    [reference]
  );

  const [thread, setThread] = useState<PlanningDiscussionThreadNode[]>([]);
  const [authStatus, setAuthStatus] = useState<AuthStatus>('checking');
  const [currentUser, setCurrentUser] = useState<DiscussionCurrentUser | null>(null);
  const [draft, setDraft] = useState('');
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editingDraft, setEditingDraft] = useState('');
  const [replyingToCommentId, setReplyingToCommentId] = useState<string | null>(null);
  const [replyDraft, setReplyDraft] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionNotice, setActionNotice] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isLoadingThread, setIsLoadingThread] = useState(false);
  const [isLoadingMoreThread, setIsLoadingMoreThread] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [busyCommentId, setBusyCommentId] = useState<string | null>(null);
  const [threadHasMore, setThreadHasMore] = useState(false);
  const [threadNextBefore, setThreadNextBefore] = useState<string | null>(null);
  const [threadCommentCount, setThreadCommentCount] = useState(0);
  const [threadRootCount, setThreadRootCount] = useState(0);

  // #3 – auto-grow ref for main composer
  const composerRef = useRef<HTMLTextAreaElement>(null);

  // #7 – ref to the thread list for scrolling after post
  const threadListRef = useRef<HTMLDivElement>(null);

  // #4 – auto-dismiss actionError after 6 seconds
  useEffect(() => {
    if (!actionError) return;
    const id = setTimeout(() => setActionError(null), 6000);
    return () => clearTimeout(id);
  }, [actionError]);

  const syncThreadFromCache = useCallback(() => {
    if (!normalizedReference) {
      setThread([]); setThreadHasMore(false); setThreadNextBefore(null);
      setThreadCommentCount(0); setThreadRootCount(0);
      return;
    }
    const payload = getDiscussionThreadPayload(normalizedReference);
    setThread(getDiscussionThread(normalizedReference));
    setThreadHasMore(payload.hasMore);
    setThreadNextBefore(payload.nextBefore);
    setThreadCommentCount(payload.commentCount);
    setThreadRootCount(payload.threadCount);
  }, [normalizedReference]);

  const loadThread = useCallback(async (preferAuthenticated: boolean) => {
    if (!normalizedReference) { setThread([]); setLoadError(null); return; }
    setIsLoadingThread(true);
    try {
      const payload = await hydrateDiscussion(normalizedReference, { preferAuthenticated, council });
      setThread(payload.comments);
      setThreadHasMore(payload.hasMore);
      setThreadNextBefore(payload.nextBefore);
      setThreadCommentCount(payload.commentCount);
      setThreadRootCount(payload.threadCount);
      setLoadError(null);
    } catch (error) {
      console.error('Failed to hydrate planning discussion:', error);
      setLoadError('Unable to load comments right now.');
    } finally {
      setIsLoadingThread(false);
    }
  }, [council, normalizedReference]);

  const loadMoreThreads = useCallback(async () => {
    if (!normalizedReference || !threadNextBefore || isLoadingMoreThread) return;
    setIsLoadingMoreThread(true);
    try {
      const payload = await hydrateDiscussion(normalizedReference, {
        preferAuthenticated: authStatus === 'signed-in',
        before: threadNextBefore,
        append: true,
        council,
      });
      setThread(payload.comments);
      setThreadHasMore(payload.hasMore);
      setThreadNextBefore(payload.nextBefore);
      setThreadCommentCount(payload.commentCount);
      setThreadRootCount(payload.threadCount);
      setLoadError(null);
    } catch (error) {
      console.error('Failed to load older discussion threads:', error);
      setLoadError('Unable to load older threads right now.');
    } finally {
      setIsLoadingMoreThread(false);
    }
  }, [authStatus, council, isLoadingMoreThread, normalizedReference, threadNextBefore]);

  const loadAuthState = useCallback(async () => {
    try {
      setAuthStatus('checking');
      const { getCurrentUser } = await import('aws-amplify/auth');
      const sessionUser = await getCurrentUser();
      const authUser = sessionUser as typeof sessionUser & { signInDetails?: { loginId?: string } };
      const me = await getEmailFromIdToken();
      const email = me?.email || (typeof authUser?.signInDetails?.loginId === 'string' ? authUser.signInDetails.loginId : null);
      const userId = me?.id || sessionUser?.userId || sessionUser?.username;
      if (!userId) throw new Error('Missing user identity');
      setCurrentUser({ userId, email, displayName: formatAuthorLabel(email, sessionUser?.username || null) });
      setAuthStatus('signed-in');
    } catch {
      setCurrentUser(null);
      setAuthStatus('signed-out');
    }
  }, []);

  useEffect(() => { syncThreadFromCache(); }, [syncThreadFromCache]);
  useEffect(() => { loadAuthState(); }, [loadAuthState]);

  // Restore draft (now from localStorage with 24h TTL)
  useEffect(() => {
    if (!normalizedReference) return;
    const restoredDraft = loadDiscussionDraftState(normalizedReference);
    if (!restoredDraft) return;
    setDraft(restoredDraft.draft);
    setReplyingToCommentId(restoredDraft.replyingToCommentId);
    setReplyDraft(restoredDraft.replyDraft);
    setActionNotice('Your draft was restored.');
  }, [normalizedReference]);

  useEffect(() => {
    const unsubscribe = subscribeToDiscussionUpdates(syncThreadFromCache);
    return unsubscribe;
  }, [syncThreadFromCache]);

  useEffect(() => { void loadThread(authStatus === 'signed-in'); }, [authStatus, loadThread]);

  useEffect(() => {
    let unlisten: (() => void) | undefined;
    (async () => {
      try {
        const { Hub } = await import('aws-amplify/utils');
        unlisten = Hub.listen('auth', ({ payload }) => {
          const event = payload?.event;
          if (event === 'signedIn' || event === 'signedOut') loadAuthState();
        });
      } catch { /* Hub optional */ }
    })();
    return () => { if (unlisten) unlisten(); };
  }, [loadAuthState]);

  const resetEditState = useCallback(() => { setEditingCommentId(null); setEditingDraft(''); }, []);
  const resetReplyState = useCallback(() => { setReplyingToCommentId(null); setReplyDraft(''); }, []);
  const handleRequireAuth = useCallback((message: string) => { setActionError(message); setActionNotice(null); }, []);

  const handleSignIn = useCallback(async () => {
    setActionError(null); setActionNotice(null);
    try {
      saveDiscussionDraftState(normalizedReference, { draft, replyingToCommentId, replyDraft });
      const returnTo = rememberDiscussionAuthReturn(normalizedReference);
      window.location.assign(createAuthPath('sign-in', returnTo));
    } catch (error) {
      console.error('Discussion sign in redirect failed:', error);
      setActionError('Unable to start sign in right now.');
    }
  }, [draft, normalizedReference, replyingToCommentId, replyDraft]);

  const handleCreateAccount = useCallback(async () => {
    setActionError(null); setActionNotice(null);
    try {
      saveDiscussionDraftState(normalizedReference, { draft, replyingToCommentId, replyDraft });
      const returnTo = rememberDiscussionAuthReturn(normalizedReference);
      window.location.assign(createAuthPath('sign-up', returnTo));
    } catch (error) {
      console.error('Discussion sign up redirect failed:', error);
      setActionError('Unable to open account creation right now.');
    }
  }, [draft, normalizedReference, replyingToCommentId, replyDraft]);

  const handleSubmitComment = useCallback(async () => {
    if (!currentUser || !normalizedReference) { setActionError('Sign in to join the discussion.'); return; }
    const nextBody = draft.trim();
    if (!nextBody) { setActionError('Add a comment before posting.'); return; }

    setActionError(null); setActionNotice(null); setIsSubmitting(true);

    // #9 – optimistic insert
    const optimisticId = `optimistic-${Date.now()}`;
    const optimisticComment: PlanningDiscussionThreadNode = {
      id: optimisticId,
      reference: normalizedReference,
      parentId: null,
      body: nextBody,
      status: 'visible',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      author: { displayName: currentUser.displayName },
      votes: { upCount: 0, downCount: 0, score: 0, currentUserVote: null },
      canEdit: true,
      canDelete: true,
      replies: [],
    };
    setThread((prev) => [optimisticComment, ...prev]);
    setDraft('');
    if (composerRef.current) { composerRef.current.style.height = 'auto'; }

    // #7 – scroll the list to top to show the optimistic comment
    setTimeout(() => {
      threadListRef.current?.firstElementChild?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 50);

    try {
      const result = await addDiscussionComment({
        reference: normalizedReference,
        body: nextBody,
        authorDisplayName: currentUser.displayName,
        council,
      });
      // Replace optimistic comment with real one from cache sync
      syncThreadFromCache();
      if (result.moderation?.status === 'hidden') {
        setActionNotice(result.moderation.message || 'Your comment has been held for review.');
      }
    } catch (error) {
      // Roll back optimistic insert
      setThread((prev) => prev.filter((c) => c.id !== optimisticId));
      setDraft(nextBody);
      console.error('Failed to create discussion comment:', error);
      const { status: apiStatus, detail } = getAccountsApiDetail(error);
      if (apiStatus === 409) { setActionError(getFriendlyDuplicateMessage(detail)); return; }
      setActionError('Unable to post this comment right now.');
    } finally {
      setIsSubmitting(false);
    }
  }, [council, currentUser, draft, normalizedReference, syncThreadFromCache]);

  const handleSubmitReply = useCallback(async (parentId: string) => {
    if (!currentUser || !normalizedReference) { setActionError('Sign in to reply to comments.'); return; }
    const nextBody = replyDraft.trim();
    if (!nextBody) { setActionError('Add a reply before posting.'); return; }

    setBusyCommentId(parentId); setActionError(null); setActionNotice(null);
    try {
      const result = await addDiscussionComment({
        reference: normalizedReference, body: nextBody, parentId,
        authorDisplayName: currentUser.displayName,
        council,
      });
      resetReplyState();
      if (result.moderation?.status === 'hidden') {
        setActionNotice(result.moderation.message || 'Your reply has been held for review.');
      }
    } catch (error) {
      console.error('Failed to create reply comment:', error);
      const { status: apiStatus, detail } = getAccountsApiDetail(error);
      if (apiStatus === 409) { setActionError(getFriendlyDuplicateMessage(detail)); return; }
      setActionError('Unable to post this reply right now.');
    } finally {
      setBusyCommentId(null);
    }
  }, [council, currentUser, normalizedReference, replyDraft, resetReplyState]);

  const handleSaveEdit = useCallback(async () => {
    if (!currentUser || !editingCommentId || !normalizedReference) return;
    const nextBody = editingDraft.trim();
    if (!nextBody) { setActionError('Edited comment cannot be empty.'); return; }

    setBusyCommentId(editingCommentId); setActionError(null); setActionNotice(null);
    try {
      const result = await updateDiscussionComment({ commentId: editingCommentId, body: nextBody });
      resetEditState();
      if (result.moderation?.status === 'hidden') {
        setActionNotice(result.moderation.message || 'Your edit has been held for review.');
      }
    } catch (error) {
      console.error('Failed to update discussion comment:', error);
      const { status: apiStatus, detail } = getAccountsApiDetail(error);
      if (apiStatus === 409) { setActionError(getFriendlyDuplicateMessage(detail)); return; }
      setActionError('Unable to save your edit right now.');
    } finally {
      setBusyCommentId(null);
    }
  }, [currentUser, editingCommentId, editingDraft, normalizedReference, resetEditState]);

  const handleDeleteComment = useCallback(async (commentId: string) => {
    // #1 – confirmation is now inline in DiscussionCommentItem; this fires after confirm
    if (!currentUser || !normalizedReference) return;
    setBusyCommentId(commentId); setActionError(null);
    try {
      await deleteDiscussionComment({ commentId });
      resetEditState(); resetReplyState();
    } catch (error) {
      console.error('Failed to delete discussion comment:', error);
      setActionError('Unable to delete that comment right now.');
    } finally {
      setBusyCommentId(null);
    }
  }, [currentUser, normalizedReference, resetEditState, resetReplyState]);

  const handleVote = useCallback(async (commentId: string, value: 'up' | 'down') => {
    if (!currentUser || !normalizedReference) { setActionError('Sign in to vote on comments.'); return; }
    setBusyCommentId(commentId); setActionError(null);
    try {
      await toggleDiscussionVote({ commentId, value });
    } catch (error) {
      console.error('Failed to vote on discussion comment:', error);
      setActionError('Unable to update your vote right now.');
    } finally {
      setBusyCommentId(null);
    }
  }, [currentUser, normalizedReference]);

  const handleEditComment = useCallback((commentId: string) => {
    const find = (nodes: PlanningDiscussionThreadNode[]): PlanningDiscussionThreadNode | null => {
      for (const node of nodes) {
        if (node.id === commentId) return node;
        const nested = find(node.replies);
        if (nested) return nested;
      }
      return null;
    };
    const target = find(thread);
    if (!target) return;
    setActionError(null); setReplyingToCommentId(null); setReplyDraft('');
    setEditingCommentId(commentId); setEditingDraft(target.body);
  }, [thread]);

  const handleReplyToComment = useCallback((commentId: string) => {
    setActionError(null); resetEditState();
    setReplyingToCommentId((current) => (current === commentId ? null : commentId));
    setReplyDraft('');
  }, [resetEditState]);

  // #2 – Cmd/Ctrl+Enter on main composer
  const handleComposerKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') { e.preventDefault(); handleSubmitComment(); }
  };

  const loadedCommentCount = useMemo(() => countThreadNodes(thread), [thread]);
  const totalCommentCount = Math.max(threadCommentCount, loadedCommentCount);
  const rootThreadCount = Math.max(threadRootCount, thread.length);
  const currentUserInitials = getAuthorInitials(currentUser?.displayName || null);

  return (
    <section className="overflow-hidden rounded-[22px] border border-[var(--c-border)] bg-[var(--c-surface)]">
      {/* Header */}
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-[var(--c-border)] bg-[var(--c-bg)]/55 px-4 py-3.5 sm:px-5">
        <div className="flex min-w-0 items-center gap-2.5">
          <span className="inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full border border-[var(--c-accent)]/25 bg-[var(--c-accent)]/12 text-[var(--c-accent)]">
            <MessageSquareText size={15} />
          </span>
          <div className="min-w-0">
            <h4 className="text-base font-semibold leading-tight text-[var(--c-text-primary)]">Discussion</h4>
            {/* #10 – council portal link */}
            <p className="mt-0.5 text-[11px] leading-snug text-[var(--c-text-tertiary)]">
              Share local context. Keep it factual — for formal objections or support comments,{' '}
              {councilUrl ? (
                <a
                  href={councilUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-0.5 underline decoration-dotted underline-offset-2 hover:text-[var(--c-accent)]"
                >
                  visit the council portal
                  <ExternalLink size={9} />
                </a>
              ) : (
                'visit the official application on the council portal'
              )}
              .
            </p>
          </div>
        </div>

        <div className="flex flex-shrink-0 items-center gap-1.5 text-[11px]">
          <span className="inline-flex items-center gap-1 rounded-full border border-[var(--c-border)] bg-[var(--c-surface)] px-2.5 py-1 font-medium text-[var(--c-text-secondary)]">
            <MessageSquareText size={11} className="opacity-70" />
            {totalCommentCount}
          </span>
          {rootThreadCount > 0 ? (
            <span className="rounded-full border border-[var(--c-border)] bg-[var(--c-surface)] px-2.5 py-1 font-medium text-[var(--c-text-secondary)]">
              {rootThreadCount} {rootThreadCount === 1 ? 'thread' : 'threads'}
            </span>
          ) : null}
        </div>
      </header>

      <div className="px-4 py-4 sm:px-5">
        {!normalizedReference ? (
          <div className="rounded-2xl border border-dashed border-[var(--c-border)] bg-[var(--c-bg)] px-4 py-6 text-center text-sm text-[var(--c-text-secondary)]">
            Discussion is unavailable for this item — there is no planning reference to key comments against.
          </div>
        ) : (
          <div className="space-y-4">
            {/* Composer */}
            {authStatus === 'signed-in' && currentUser ? (
              <div className="rounded-2xl border border-[var(--c-border)] bg-[var(--c-bg)]/85 p-3 sm:p-4">
                <div className="flex gap-3">
                  <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full border border-[var(--c-accent)]/30 bg-[var(--c-accent)]/12 text-[11px] font-semibold uppercase tracking-[0.12em] text-[var(--c-accent)]">
                    {currentUserInitials}
                  </div>
                  <div className="min-w-0 flex-1">
                    {/* #3 – auto-grow composer textarea */}
                    <textarea
                      ref={composerRef}
                      value={draft}
                      onChange={(e) => { setDraft(e.target.value); autoGrow(e.target); }}
                      onKeyDown={handleComposerKeyDown}
                      rows={3}
                      placeholder={thread.length === 0
                        ? 'Be the first to share your perspective on this application...'
                        : 'Add a comment about this application...'}
                      className="min-h-[88px] w-full resize-none overflow-hidden rounded-xl border border-[var(--c-border)] bg-[var(--c-surface)] px-3 py-2.5 text-sm leading-6 text-[var(--c-text-primary)] outline-none transition-colors duration-150 placeholder:text-[var(--c-text-tertiary)] focus:border-[var(--c-accent)]"
                    />
                    <div className="mt-2 flex items-center justify-between gap-3">
                      <p className="text-[11px] text-[var(--c-text-tertiary)]">
                        Posting as{' '}
                        <span className="font-medium text-[var(--c-text-secondary)]">{currentUser.displayName}</span>
                        <span className="ml-2 opacity-60">· ⌘↵ to post</span>
                      </p>
                      <button
                        type="button"
                        onClick={handleSubmitComment}
                        disabled={isSubmitting || draft.trim().length === 0}
                        className="inline-flex items-center justify-center gap-1.5 rounded-full bg-[var(--c-accent)] px-4 py-1.5 text-xs font-semibold text-[var(--c-bg)] transition-all duration-150 hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {isSubmitting ? <Loader size={12} className="animate-spin" /> : <Send size={12} />}
                        Post comment
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex flex-col gap-3 rounded-2xl border border-[var(--c-border)] bg-[var(--c-bg)]/85 p-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-start gap-3 sm:items-center">
                  <span className="hidden h-9 w-9 flex-shrink-0 items-center justify-center rounded-full border border-[var(--c-accent)]/25 bg-[var(--c-accent)]/12 text-[var(--c-accent)] sm:inline-flex">
                    <LogIn size={15} />
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-[var(--c-text-primary)]">Sign in to join the thread</p>
                    <p className="mt-0.5 text-[11px] leading-snug text-[var(--c-text-tertiary)]">
                      Reading is open to everyone — you only need an account to post or reply.
                    </p>
                  </div>
                </div>
                <div className="flex flex-shrink-0 flex-wrap gap-2 sm:justify-end">
                  <button
                    type="button"
                    onClick={handleSignIn}
                    disabled={authStatus === 'checking'}
                    className="inline-flex items-center justify-center gap-1.5 rounded-full border border-[var(--c-border)] bg-[var(--c-surface)] px-3.5 py-1.5 text-xs font-medium text-[var(--c-text-primary)] transition-colors duration-150 hover:border-[var(--c-accent)] hover:text-[var(--c-accent)] disabled:opacity-60"
                  >
                    {authStatus === 'checking' ? <Loader size={12} className="animate-spin" /> : <LogIn size={12} />}
                    Sign in
                  </button>
                  <button
                    type="button"
                    onClick={handleCreateAccount}
                    className="inline-flex items-center justify-center gap-1.5 rounded-full bg-[var(--c-accent)] px-3.5 py-1.5 text-xs font-semibold text-[var(--c-bg)] transition-all duration-150 hover:brightness-110"
                  >
                    <UserPlus size={12} />
                    Create account
                  </button>
                </div>
              </div>
            )}

            {/* Inline notices — #4 auto-dismissed via useEffect */}
            {actionError ? (
              <div className="rounded-xl border border-[var(--c-error)]/30 bg-[var(--c-error)]/8 px-3 py-2 text-xs text-[var(--c-error)]">
                {actionError}
              </div>
            ) : null}
            {actionNotice ? (
              <div className="rounded-xl border border-[var(--c-accent)]/25 bg-[var(--c-accent)]/10 px-3 py-2 text-xs text-[var(--c-accent)]">
                {actionNotice}
              </div>
            ) : null}
            {loadError ? (
              <div className="rounded-xl border border-[var(--c-error)]/30 bg-[var(--c-error)]/8 px-3 py-2 text-xs text-[var(--c-error)]">
                {loadError}
              </div>
            ) : null}

            {/* Thread */}
            <div>
              {isLoadingThread && thread.length === 0 ? (
                <div className="flex items-center justify-center gap-2 rounded-2xl border border-dashed border-[var(--c-border)] bg-[var(--c-bg)]/55 px-5 py-10 text-sm text-[var(--c-text-secondary)]">
                  <Loader size={14} className="animate-spin" />
                  Loading comments...
                </div>
              ) : thread.length === 0 ? (
                <div className="flex flex-col items-center gap-2 rounded-2xl border border-dashed border-[var(--c-border)] bg-[var(--c-bg)]/55 px-5 py-10 text-center">
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-[var(--c-border)] bg-[var(--c-surface)] text-[var(--c-text-tertiary)]">
                    <MessageSquareText size={18} />
                  </span>
                  <p className="text-sm font-medium text-[var(--c-text-primary)]">No comments yet</p>
                  <p className="max-w-sm text-[11px] leading-snug text-[var(--c-text-tertiary)]">
                    {authStatus === 'signed-in'
                      ? 'Be the first to share your perspective on this application.'
                      : 'Sign in above to be the first to share your perspective.'}
                  </p>
                </div>
              ) : (
                <>
                  <div
                    ref={threadListRef}
                    className="divide-y divide-[var(--c-border)]/55"
                    style={{ contentVisibility: 'auto', containIntrinsicSize: '960px' }}
                  >
                    {thread.map((comment) => (
                      <DiscussionCommentItem
                        key={comment.id}
                        authStatus={authStatus}
                        busyCommentId={busyCommentId}
                        comment={comment}
                        currentUser={currentUser}
                        depth={0}
                        editingCommentId={editingCommentId}
                        editingDraft={editingDraft}
                        onDeleteComment={handleDeleteComment}
                        onEditComment={handleEditComment}
                        onReplyDraftChange={setReplyDraft}
                        onReplyToComment={handleReplyToComment}
                        onRequireAuth={handleRequireAuth}
                        onSaveEdit={handleSaveEdit}
                        onSubmitReply={handleSubmitReply}
                        onUpdateEditingDraft={setEditingDraft}
                        onVote={handleVote}
                        replyingToCommentId={replyingToCommentId}
                        replyDraft={replyDraft}
                        resetEditState={resetEditState}
                        resetReplyState={resetReplyState}
                      />
                    ))}
                  </div>
                  {threadHasMore ? (
                    <div className="mt-3 flex justify-center">
                      <button
                        type="button"
                        onClick={loadMoreThreads}
                        disabled={isLoadingMoreThread}
                        className="inline-flex items-center justify-center gap-2 rounded-full border border-[var(--c-border)] bg-[var(--c-bg)] px-4 py-1.5 text-xs font-medium text-[var(--c-text-secondary)] transition-colors duration-150 hover:border-[var(--c-accent)] hover:text-[var(--c-accent)] disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {isLoadingMoreThread ? <Loader size={12} className="animate-spin" /> : null}
                        Load older threads
                      </button>
                    </div>
                  ) : null}
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
